package de.xtl.retroguide.question.scenes

import androidx.compose.animation.core.FastOutSlowInEasing
import androidx.compose.animation.core.LinearEasing
import androidx.compose.animation.core.RepeatMode
import androidx.compose.animation.core.animateFloat
import androidx.compose.animation.core.infiniteRepeatable
import androidx.compose.animation.core.rememberInfiniteTransition
import androidx.compose.animation.core.tween
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.geometry.CornerRadius
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Rect
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.graphics.drawscope.DrawScope
import androidx.compose.ui.graphics.drawscope.clipPath
import androidx.compose.ui.graphics.drawscope.rotate
import androidx.compose.ui.unit.dp
import de.xtl.retroguide.protocol.AnswerCategory
import kotlin.math.PI
import kotlin.math.cos
import kotlin.math.sin

private val Sky = listOf(Color(0xFF81D4FA), Color(0xFFFFF8E1))
private val Hill = Color(0xFF7CB342)
private val HillDark = Color(0xFF558B2F)
private val Envelope = Color(0xFFE53935)
private val EnvelopeStripe = Color(0xFFFFCA28)
private val Basket = Color(0xFF8D6E63)
private val Rope = Color(0xFF5D4037)
private val StormCloud = Color(0xFF78909C)
private val StormCloudDark = Color(0xFF546E7A)
private val SunColor = Color(0xFFFFC107)

@Composable
internal fun HotAirBalloonScene(counts: Map<AnswerCategory, Int>, modifier: Modifier = Modifier) {
    val transition = rememberInfiniteTransition()
    val float by transition.animateFloat(
        initialValue = 0f,
        targetValue = (2 * PI).toFloat(),
        animationSpec = infiniteRepeatable(tween(5_000, easing = LinearEasing)),
    )
    val flame by transition.animateFloat(
        initialValue = .75f,
        targetValue = 1.15f,
        animationSpec = infiniteRepeatable(tween(260, easing = FastOutSlowInEasing), RepeatMode.Reverse),
    )
    val rays by transition.animateFloat(
        initialValue = 0f,
        targetValue = 360f,
        animationSpec = infiniteRepeatable(tween(16_000, easing = LinearEasing)),
    )
    val storm by transition.animateFloat(
        initialValue = 0f,
        targetValue = 1f,
        animationSpec = infiniteRepeatable(tween(3_200, easing = LinearEasing)),
    )
    Box(modifier.fillMaxWidth().aspectRatio(16f / 9f).clip(RoundedCornerShape(24.dp))) {
        Canvas(Modifier.fillMaxSize()) {
            drawRect(Brush.verticalGradient(Sky))
            drawDriftingClouds(float)
            drawSun(center = Offset(size.width * .82f, size.height * .4f), rotation = rays)
            drawStorm(center = Offset(size.width * .17f, size.height * .38f), phase = storm)
            drawHills()
            drawBalloon(
                center = Offset(size.width * .5f, size.height * (.36f + sin(float) * .035f)),
                sway = sin(float * 2) * 6f,
                flame = flame,
            )
        }
        SceneCornerBadges(
            topStart = AnswerCategory.STORM,
            topEnd = AnswerCategory.SUN,
            bottomStart = AnswerCategory.FIRE,
            bottomEnd = AnswerCategory.SANDBAG,
            counts = counts,
        )
    }
}

private fun DrawScope.drawDriftingClouds(phase: Float) {
    val shift = phase / (2 * PI).toFloat()
    listOf(.12f to .62f, .58f to .2f, .9f to .58f).forEachIndexed { index, (x, y) ->
        val cx = ((x + shift * (.06f + index * .02f)) % 1.1f) * size.width
        val r = size.height * (.045f + index * .008f)
        val white = Color.White.copy(alpha = .85f)
        drawCircle(white, r, Offset(cx, size.height * y))
        drawCircle(white, r * .8f, Offset(cx - r, size.height * y + r * .25f))
        drawCircle(white, r * .8f, Offset(cx + r, size.height * y + r * .25f))
    }
}

private fun DrawScope.drawSun(center: Offset, rotation: Float) {
    val radius = size.height * .09f
    rotate(rotation, pivot = center) {
        repeat(12) { ray ->
            val angle = ray * 30f * PI.toFloat() / 180f
            drawLine(
                SunColor.copy(alpha = .8f),
                start = center + Offset(cos(angle), sin(angle)) * (radius * 1.25f),
                end = center + Offset(cos(angle), sin(angle)) * (radius * (if (ray % 2 == 0) 1.75f else 1.5f)),
                strokeWidth = size.height * .012f,
                cap = StrokeCap.Round,
            )
        }
    }
    drawCircle(Brush.radialGradient(listOf(Color(0xFFFFEE58), SunColor), center, radius), radius, center)
}

private fun DrawScope.drawStorm(center: Offset, phase: Float) {
    val r = size.height * .07f
    val flash = phase < .05f || phase in .09f..0.12f
    if (flash) drawCircle(Color.White.copy(alpha = .35f), r * 4f, center)
    repeat(5) { drop ->
        val x = center.x - r * 1.3f + drop * r * .65f
        val fall = (phase * 3f + drop * .37f) % 1f
        val y = center.y + r * .9f + fall * r * 1.8f
        drawLine(Color(0xFF4FC3F7), Offset(x, y), Offset(x - r * .15f, y + r * .35f), strokeWidth = r * .08f, cap = StrokeCap.Round)
    }
    if (flash) {
        val bolt = Path().apply {
            moveTo(center.x + r * .1f, center.y + r * .5f)
            lineTo(center.x - r * .3f, center.y + r * 1.4f)
            lineTo(center.x + r * .05f, center.y + r * 1.35f)
            lineTo(center.x - r * .25f, center.y + r * 2.3f)
            lineTo(center.x + r * .45f, center.y + r * 1.1f)
            lineTo(center.x + r * .1f, center.y + r * 1.15f)
            close()
        }
        drawPath(bolt, Color(0xFFFFEB3B))
    }
    drawCircle(StormCloudDark, r, center + Offset(-r, r * .3f))
    drawCircle(StormCloudDark, r * .9f, center + Offset(r * 1.05f, r * .35f))
    drawCircle(StormCloud, r * 1.25f, center)
    drawCircle(StormCloud, r * .85f, center + Offset(-r * 1.2f, r * .35f))
    drawCircle(StormCloud, r * .8f, center + Offset(r * 1.2f, r * .4f))
}

private fun DrawScope.drawHills() {
    val back = Path().apply {
        moveTo(0f, size.height * .86f)
        quadraticTo(size.width * .25f, size.height * .74f, size.width * .5f, size.height * .86f)
        quadraticTo(size.width * .78f, size.height * .97f, size.width, size.height * .8f)
        lineTo(size.width, size.height)
        lineTo(0f, size.height)
        close()
    }
    drawPath(back, HillDark)
    val front = Path().apply {
        moveTo(0f, size.height * .94f)
        quadraticTo(size.width * .4f, size.height * .84f, size.width * .72f, size.height * .95f)
        quadraticTo(size.width * .88f, size.height * 1f, size.width, size.height * .93f)
        lineTo(size.width, size.height)
        lineTo(0f, size.height)
        close()
    }
    drawPath(front, Hill)
}

private fun DrawScope.drawBalloon(center: Offset, sway: Float, flame: Float) {
    val envelopeWidth = size.height * .4f
    val envelopeHeight = size.height * .44f
    val basketTop = center.y + envelopeHeight * .62f
    val basketSize = Size(envelopeWidth * .28f, size.height * .065f)
    rotate(sway, pivot = Offset(center.x, center.y)) {
        val envelope = Path().apply {
            moveTo(center.x, center.y + envelopeHeight * .5f)
            cubicTo(
                center.x - envelopeWidth * .25f, center.y + envelopeHeight * .3f,
                center.x - envelopeWidth * .62f, center.y - envelopeHeight * .1f,
                center.x - envelopeWidth * .45f, center.y - envelopeHeight * .32f,
            )
            cubicTo(
                center.x - envelopeWidth * .3f, center.y - envelopeHeight * .58f,
                center.x + envelopeWidth * .3f, center.y - envelopeHeight * .58f,
                center.x + envelopeWidth * .45f, center.y - envelopeHeight * .32f,
            )
            cubicTo(
                center.x + envelopeWidth * .62f, center.y - envelopeHeight * .1f,
                center.x + envelopeWidth * .25f, center.y + envelopeHeight * .3f,
                center.x, center.y + envelopeHeight * .5f,
            )
            close()
        }
        drawPath(envelope, Envelope)
        clipPath(envelope) {
            listOf(-.3f, 0f, .3f).forEach { offset ->
                drawOval(
                    EnvelopeStripe,
                    topLeft = Offset(center.x + envelopeWidth * offset - envelopeWidth * .07f, center.y - envelopeHeight * .6f),
                    size = Size(envelopeWidth * .14f, envelopeHeight * 1.2f),
                )
            }
        }
        val mouthY = center.y + envelopeHeight * .44f
        val basket = Rect(Offset(center.x - basketSize.width / 2, basketTop), basketSize)
        listOf(-1f, 1f).forEach { side ->
            drawLine(
                Rope,
                Offset(center.x + side * envelopeWidth * .09f, mouthY),
                Offset(center.x + side * basketSize.width * .45f, basketTop),
                strokeWidth = size.height * .006f,
            )
        }
        val flameHeight = (basketTop - mouthY) * .85f * flame
        val flamePath = Path().apply {
            moveTo(center.x, basketTop - flameHeight)
            quadraticTo(center.x + basketSize.width * .3f, basketTop - flameHeight * .3f, center.x, basketTop)
            quadraticTo(center.x - basketSize.width * .3f, basketTop - flameHeight * .3f, center.x, basketTop - flameHeight)
            close()
        }
        drawPath(flamePath, Brush.verticalGradient(listOf(Color(0xFFFFEB3B), Color(0xFFFF6F00)), basketTop - flameHeight, basketTop))
        drawRoundRect(Basket, basket.topLeft, basket.size, CornerRadius(size.height * .012f))
        drawLine(Rope, Offset(basket.left, basket.top + basket.height * .35f), Offset(basket.right, basket.top + basket.height * .35f), strokeWidth = size.height * .005f)
        listOf(-1f, 1f).forEach { side ->
            val hook = Offset(center.x + side * basket.width * .5f, basket.bottom - basket.height * .2f)
            rotate(-sway * 2.5f, pivot = hook) {
                val bagTop = hook + Offset(side * basket.width * .12f, basket.height * .55f)
                drawLine(Rope, hook, bagTop, strokeWidth = size.height * .004f)
                drawRoundRect(
                    Color(0xFFA1887F),
                    topLeft = bagTop - Offset(basket.width * .1f, 0f),
                    size = Size(basket.width * .2f, basket.height * .55f),
                    cornerRadius = CornerRadius(basket.width * .08f),
                )
            }
        }
    }
}
